import { useMemo, useRef } from "react";
import * as React from "react";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import type { SxProps } from "@mui/material/styles";

import { useAppSelector } from "app/hooks";
import {
  selectCurrentClinic,
  selectCurrentRegionClinicsIds,
  selectHasRegions
} from "features/auth/auth-slice";
import { useGetStaffQuery } from "services/staff";
import { StaffFilters } from "services/types/staff.type";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

type SelectProviderProps = {
  value: any;
  label?: string;
  // Scope the roster to this clinic instead of the Redux current clinic —
  // for callers whose clinic choice is a form field of their own.
  clinicId?: number;
  includeOtherClinics?: boolean;
  // Every staff member in the practice, ignoring the current clinic AND region —
  // for practice-wide admin screens (Administration → Clinics "Inbox owner").
  practiceWide?: boolean;
  onOptionSelected?: (option: { label: string; value: string } | null) => void;
  isActive?: boolean | null;
  groupName?: StaffFilters["groupName"];
  required?: boolean;
  multiple?: boolean;
  openOnFocus?: boolean;
  autoFocus?: boolean;
  enablePortal?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  sx?: SxProps;
};

export default function SelectProviderSimpleField({
  value,
  label = "Provider",
  clinicId,
  includeOtherClinics,
  practiceWide = false,
  onOptionSelected,
  isActive = null,
  groupName,
  multiple = false,
  openOnFocus = false,
  autoFocus,
  disabled,
  sx
}: SelectProviderProps) {
  const currentClinic = useAppSelector(selectCurrentClinic);
  const hasRegions = useAppSelector(selectHasRegions);
  const currentRegionClinicsIds = useAppSelector(selectCurrentRegionClinicsIds);

  const clinicIds = useMemo(() => {
    if (practiceWide) return undefined;
    const scopedClinicId = clinicId ?? currentClinic?.id;
    const clinicFilters =
      !includeOtherClinics && scopedClinicId ? [scopedClinicId] : undefined;
    if (hasRegions && includeOtherClinics) {
      return currentRegionClinicsIds;
    }
    return clinicFilters;
  }, [
    practiceWide,
    clinicId,
    currentRegionClinicsIds,
    includeOtherClinics,
    currentClinic?.id,
    hasRegions
  ]);

  const staffFilters: StaffFilters = {
    is_active: isActive,
    groupName,
    clinicIds
  };

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option: { label: string; value: string }) => option.label
  });

  const {
    data: staff = [],
    isLoading,
    isFetching
  } = useGetStaffQuery(staffFilters);

  const inputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (autoFocus) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 1);
    }
  }, [autoFocus]);

  return (
    <Autocomplete
      value={value}
      openOnFocus={openOnFocus}
      disabled={disabled}
      onChange={(_event: any, newValue: any) => {
        onOptionSelected?.(newValue);
      }}
      loading={isLoading || isFetching}
      multiple={multiple}
      disableCloseOnSelect={multiple}
      sx={sx}
      options={staff.map((staffMember) => ({
        label: staffMember.fullName,
        value: String(staffMember.id)
      }))}
      renderOption={(
        props: object,
        option: { label: string; value: string } | null,
        { selected }: any
      ) => (
        <ListItem {...props}>
          {multiple && (
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
          )}
          <ListItemText>{option?.label}</ListItemText>
        </ListItem>
      )}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      filterOptions={filterOptions}
      renderInput={(params) => (
        <TextField inputRef={inputRef} {...params} label={label} />
      )}
    />
  );
}
